import { trNormalize } from "./text";
import { normalizeCoordinateNumber } from "./coordinates";

const num = (v) => {
  if (typeof v === "string") {
    const normalized = v.trim().replace(",", ".");
    return Number.isFinite(Number(normalized)) ? Number(normalized) : null;
  }
  return Number.isFinite(Number(v)) ? Number(v) : null;
};

// my2.xlsx ham sütun adlarını görselde kullanılan isimlere eşler
const COL_REMAP = {
  "Turkcell": "Kullanılan Mobil Operatör Dağılımı % Tcell",
  "Turk Telekom": "Kullanılan Mobil Operatör Dağılımı % TurkTelekom",
  "Vodafone": "Kullanılan Mobil Operatör Dağılımı % Vodafone",
  "Apple": "Sahip Olunan Telefon Markası Dağılımı Apple",
  "GM": "Sahip Olunan Telefon Markası Dağılımı GM",
  "Huawei": "Sahip Olunan Telefon Markası Dağılımı Huawei",
  "Reeder": "Sahip Olunan Telefon Markası Dağılımı Reeder",
  "Samsung": "Sahip Olunan Telefon Markası Dağılımı Samsung",
  "Xiaomi": "Sahip Olunan Telefon Markası Dağılımı Xiaomi",
  "A": "Sosyo Ekonomik Segment Dağılımı A",
  "B": "Sosyo Ekonomik Segment Dağılımı B",
  "C": "Sosyo Ekonomik Segment Dağılımı C",
  "DE": "Sosyo Ekonomik Segment Dağılımı DE",
  "University_Student % ": "Üniversite Öğrencisi Oranı %",
};

const WEEKLY_UNIQUE_USERS_COLUMN = "Ortalama haftalık tekil kişi(mart-agustos 2025)";

function getFirstValue(row, keys) {
  const entries = Object.entries(row || {});
  const normalizedKeys = keys.map(trNormalize);
  const match = entries.find(([key]) => normalizedKeys.includes(trNormalize(key)));
  return match?.[1];
}

// normalizeCoordinateNumber, num()'un aksine boş/geçersiz değerleri undefined yapar ve
// Türkiye enlem/boylam aralığı dışındaki değerleri reddeder. Koordinatsız bir adres
// num() ile sessizce (0,0) sayılıp mobility'deki başka bir boş satırla yanlışlıkla
// eşleşebiliyordu; bu yüzden koordinatlar için ayrı, doğrulamalı bir yol kullanıyoruz.
function getSiteCoordinate(site, axis) {
  const keys = axis === "lat"
    ? ["lat", "latitude", "enlem"]
    : ["lng", "lon", "long", "longitude", "longtitude", "boylam"];
  return normalizeCoordinateNumber(getFirstValue(site, keys), axis);
}

function coordinateKey(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `${lat.toFixed(6)}|${lng.toFixed(6)}`;
}
/**
 * Mobility satırlarını normalize eder.
 * ÖNEMLİ: dataset.type'a güvenmez; her dataset'i tarar,
 * 'Adres' + haftalık tekil kişi kolonu bulunursa mobilite olarak işler.
 */
export function normalizeMobilityRows(datasets) {
  const out = [];

  (datasets || []).forEach((dataset) => {
    const rows = dataset?.rows || [];
    if (!rows.length) return;

    // kolonları normalize et
    const cols = Object.keys(rows[0]);
    const ncols = cols.map((c) => trNormalize(c));

    const idxAdres =
      ncols.findIndex((c) => c.includes("adres")) >= 0
        ? ncols.findIndex((c) => c.includes("adres"))
        : ncols.findIndex((c) => c.includes("unnamed")) >= 0
        ? ncols.findIndex((c) => c.includes("unnamed"))
        : ncols.findIndex((c) => c === "__empty");

    const weeklyUsersCol = cols.find((c) => String(c).trim() === WEEKLY_UNIQUE_USERS_COLUMN);
    const hasUsersHint = Boolean(weeklyUsersCol);

    // mobilite sinyali yoksa es geç
    if (idxAdres < 0 || !hasUsersHint) return;

    const adresCol = cols[idxAdres];

    rows.forEach((r) => {
      const rawAddr = r[adresCol];
      if (!rawAddr) return;
      const addrStr = String(rawAddr).trim();
      // başlık satırını at
      if (addrStr.toLowerCase() === "adres") return;

      const address = addrStr;

      const city =
        r.IL ?? r["İl"] ?? r.SEHIR ?? r.SEHİR ?? r["Şehir"] ?? r.City ?? "";

      const users = num(r[weeklyUsersCol]) ?? 0;
      const latitude = normalizeCoordinateNumber(getFirstValue(r, ["lat", "latitude", "enlem"]), "lat");
      const longitude = normalizeCoordinateNumber(getFirstValue(r, ["lng", "lon", "long", "longitude", "longtitude", "boylam"]), "lng");

      // 0..1 aralığındaki yüzdelik/pay alanlarını yakala; ham sütun adlarını görsel isimlere dönüştür
      const shares = {};
      Object.entries(r).forEach(([k, v]) => {
        const n = Number(v);
        if (Number.isFinite(n) && n >= 0 && n <= 1) {
          const displayKey = COL_REMAP[k] ?? k;
          shares[displayKey] = n;
        }
      });
      out.push({
        address,
        city,
        latitude,
        longitude,
        users: users || 0,
        shares,
        __dataset: dataset.name,
      });
    });
  });

  return out;
}

/** site ile mobility satırı arasında yalnızca birebir koordinat (lat/lng) eşleşmesi arar. */
export function matchMobilityByAddress(site, mobRows) {
  const latitude = getSiteCoordinate(site, "lat");
  const longitude = getSiteCoordinate(site, "lng");
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];

  const siteCoordinateKey = coordinateKey(latitude, longitude);
  const coordinateMatch = (mobRows || []).find((row) => coordinateKey(row.latitude, row.longitude) === siteCoordinateKey);
  return coordinateMatch ? [{ ...coordinateMatch, __matchType: "coordinate" }] : [];
}

/** Ağırlıklı paylar + toplam haftalık kullanıcı + top-10 adres */
export function aggregateMobilityRows(rows) {
  const totalUsers = rows.reduce((s, r) => s + (Number(r.users) || 0), 0);

  const wavg = {};
  const sumW = totalUsers || 1;
  rows.forEach((r) => {
    const w = Number(r.users) || 0;
    Object.entries(r.shares || {}).forEach(([k, v]) => {
      if (!Number.isFinite(v)) return;
      wavg[k] = (wavg[k] || 0) + v * w;
    });
  });
  Object.keys(wavg).forEach((k) => (wavg[k] = wavg[k] / sumW));

  const topPoints = [...rows]
    .map((r) => ({ address: r.address || "-", users: Number(r.users) || 0 }))
    .sort((a, b) => b.users - a.users)
    .slice(0, 10);

  return { totalUsers, shares: wavg, topPoints };
}

const SES_GROUPS = [
  { key: "A", label: "A" },
  { key: "B", label: "B" },
  { key: "C", label: "C" },
  { key: "DE", label: "D-E" },
];

/** SES paylarını ve paya karşılık gelen tahmini haftalık erişimi tek formatta döndürür. */
export function getSesMetrics(aggregate) {
  const shares = aggregate?.shares || {};
  const totalUsers = Number(aggregate?.totalUsers) || 0;
  return SES_GROUPS.map((group) => {
    const suffix = ` ${trNormalize(group.key)}`;
    const entry = Object.entries(shares).find(([name]) => {
      const normalized = trNormalize(name);
      return normalized.includes("sosyo ekonomik segment dagilimi") && normalized.endsWith(suffix);
    });
    const share = Number(entry?.[1]) || 0;
    return {
      key: group.key,
      label: group.label,
      share,
      reach: Math.round(totalUsers * share),
    };
  });
}

export function filterByCities(rows, cities) {
  const wanted = new Set((cities || []).map(trNormalize));
  if (!wanted.size) return [];
  return rows.filter((r) => wanted.has(trNormalize(r.city || "")));
}
